/**
 * Portrait answer cards: friendly half-body characters drawn in code, for answers about the person taking the
 * quiz and the people moving with them (gender, household, age…). Larger than the icon library
 * (illustrations.js): a 160 x 150 picture that fills the bottom of a tall card, the answer's label top left.
 *
 * Names can carry "{sex}" (e.g. "hh-solo-{sex}"); the quiz swaps in the answer to its gender question
 * ("male" / "female") so "you" look like you. portrait(name, sex) returns SVG markup, or '' if unknown.
 */

const SKIN = {
  deep: ['#6b4129', '#55331f'],
  brown: ['#8d5b3e', '#74482f'],
  warm: ['#a86e4a', '#8f5a3a'],
  light: ['#c08a62', '#a6714d']
};
const HAIR = '#1d1612';
const GREY = '#bdb6ad';
const INK = '#2a1b14';
const GOLD = 'var(--vp-gold,#d9a441)';
const GREEN = 'var(--vp-green,#2e6b5a)';
const TEAL = 'var(--vp-teal,#3f8f88)';
const CORAL = 'var(--vp-coral,#d9735b)';
const PLUM = 'var(--vp-plum,#7a4b78)';
const NAVY = 'var(--vp-navy,#33507a)';

/**
 * One character, centred on cx with the bottom of the shoulders at y=150 (the picture's bottom edge).
 * o: { sex, skin, shirt, hair: 'short'|'braids'|'wrap'|'puffs'|'fade', beard, grey, glasses, earrings, scale }
 */
function character(cx, o) {
  const s = o.scale || 1;
  const [skin, shade] = SKIN[o.skin || 'brown'];
  const hair = o.grey ? GREY : HAIR;
  const bottom = o.base || 150;
  const headY = bottom - 76 * s; // centre of the head
  const X = dx => (cx + dx * s).toFixed(1);
  const Y = dy => (headY + dy * s).toFixed(1);
  const out = [];

  // long hair falls behind the head and shoulders
  if (o.hair === 'braids') {
    out.push(`<path d="M${X(-30)} ${Y(-6)}C${X(-33)} ${Y(-34)} ${X(-14)} ${Y(-38)} ${X(0)} ${Y(-38)}C${X(14)} ${Y(-38)} ${X(33)} ${Y(-34)} ${X(30)} ${Y(-6)}L${X(35)} ${Y(46)}Q${X(30)} ${Y(52)} ${X(25)} ${Y(46)}Q${X(20)} ${Y(52)} ${X(15)} ${Y(46)}L${X(-15)} ${Y(46)}Q${X(-20)} ${Y(52)} ${X(-25)} ${Y(46)}Q${X(-30)} ${Y(52)} ${X(-35)} ${Y(46)}Z" fill="${hair}"/>`);
    for (let i = -3; i <= 3; i++) if (i) out.push(`<path d="M${X(i * 9)} ${Y(4)}V${Y(46)}" stroke="#2f2520" stroke-width="${(1.6 * s).toFixed(1)}" stroke-linecap="round"/>`);
  }

  // shoulders and top
  out.push(`<path d="M${X(-52)} ${bottom}C${X(-52)} ${Y(56)} ${X(-36)} ${Y(46)} ${X(-16)} ${Y(43)}H${X(16)}C${X(36)} ${Y(46)} ${X(52)} ${Y(56)} ${X(52)} ${bottom}Z" fill="${o.shirt}"/>`);
  const sw = n => (n * s).toFixed(1);
  const outfit = o.outfit || 'tee';
  // a shirt showing under a blazer or cardigan, or a school shirt
  if (outfit === 'blazer' || outfit === 'cardigan' || outfit === 'uniform' || outfit === 'collar') {
    out.push(`<path d="M${X(-13)} ${Y(43)}L${X(0)} ${Y(68)}L${X(13)} ${Y(43)}Z" fill="${o.inner || '#f4f1ea'}"/>`);
  }
  // neck and neckline
  out.push(`<path d="M${X(-10)} ${Y(18)}H${X(10)}V${Y(42)}L${X(0)} ${Y(56)}L${X(-10)} ${Y(42)}Z" fill="${shade}"/>`);
  if (outfit === 'collar' || outfit === 'uniform') {
    out.push(`<path d="M${X(-12)} ${Y(41)}L${X(-1)} ${Y(57)}L${X(-15)} ${Y(53)}Z M${X(12)} ${Y(41)}L${X(1)} ${Y(57)}L${X(15)} ${Y(53)}Z" fill="#ffffff" stroke="rgba(0,0,0,.12)" stroke-width="${sw(0.8)}"/>`);
  }
  if (outfit === 'uniform') {
    out.push(`<path d="M${X(-3)} ${Y(56)}H${X(3)}L${X(5)} ${Y(74)}L${X(0)} ${Y(78)}L${X(-5)} ${Y(74)}Z" fill="${o.tie || NAVY}"/>`);
  }
  if (outfit === 'blazer' || outfit === 'cardigan') {
    const lap = outfit === 'blazer' ? 'rgba(0,0,0,.22)' : 'rgba(255,255,255,.18)';
    out.push(`<path d="M${X(-17)} ${Y(43)}L${X(-1)} ${Y(70)}L${X(-6)} ${Y(78)}L${X(-28)} ${Y(50)}Z M${X(17)} ${Y(43)}L${X(1)} ${Y(70)}L${X(6)} ${Y(78)}L${X(28)} ${Y(50)}Z" fill="${lap}"/>`);
    if (outfit === 'cardigan') out.push(`<circle cx="${X(-4)}" cy="${Y(72)}" r="${sw(1.6)}" fill="rgba(0,0,0,.3)"/>`);
  }
  if (outfit === 'hoodie') {
    out.push(`<path d="M${X(-22)} ${Y(44)}C${X(-16)} ${Y(58)} ${X(16)} ${Y(58)} ${X(22)} ${Y(44)}C${X(14)} ${Y(50)} ${X(-14)} ${Y(50)} ${X(-22)} ${Y(44)}Z" fill="rgba(0,0,0,.18)"/>`);
    out.push(`<path d="M${X(-6)} ${Y(54)}V${Y(70)}M${X(6)} ${Y(54)}V${Y(70)}" stroke="#f4f1ea" stroke-width="${sw(1.8)}" stroke-linecap="round"/>`);
  }
  if (o.straps) {
    out.push(`<path d="M${X(-30)} ${Y(47)}C${X(-27)} ${Y(60)} ${X(-26)} ${Y(70)} ${X(-26)} ${Y(78)}M${X(30)} ${Y(47)}C${X(27)} ${Y(60)} ${X(26)} ${Y(70)} ${X(26)} ${Y(78)}" stroke="${o.straps}" stroke-width="${sw(6)}" fill="none" stroke-linecap="round"/>`);
  }

  // ears, head
  out.push(`<ellipse cx="${X(-24)}" cy="${Y(4)}" rx="${(5 * s).toFixed(1)}" ry="${(7 * s).toFixed(1)}" fill="${shade}"/><ellipse cx="${X(24)}" cy="${Y(4)}" rx="${(5 * s).toFixed(1)}" ry="${(7 * s).toFixed(1)}" fill="${shade}"/>`);
  out.push(`<ellipse cx="${X(0)}" cy="${Y(0)}" rx="${(24 * s).toFixed(1)}" ry="${(28 * s).toFixed(1)}" fill="${skin}"/>`);

  // hair on top
  if (o.hair === 'short' || o.hair === 'fade') {
    const t = o.hair === 'fade' ? -30 : -33;
    out.push(`<path d="M${X(-24.5)} ${Y(-4)}C${X(-26)} ${Y(-22)} ${X(-16)} ${Y(t)} ${X(0)} ${Y(t)}C${X(16)} ${Y(t)} ${X(26)} ${Y(-22)} ${X(24.5)} ${Y(-4)}C${X(22)} ${Y(-12)} ${X(19)} ${Y(-16)} ${X(14)} ${Y(-18)}C${X(6)} ${Y(-21)} ${X(-6)} ${Y(-21)} ${X(-14)} ${Y(-18)}C${X(-19)} ${Y(-16)} ${X(-22)} ${Y(-12)} ${X(-24.5)} ${Y(-4)}Z" fill="${hair}"/>`);
  } else if (o.hair === 'braids') {
    out.push(`<path d="M${X(-25)} ${Y(2)}C${X(-27)} ${Y(-28)} ${X(-10)} ${Y(-32)} ${X(2)} ${Y(-31)}C${X(18)} ${Y(-32)} ${X(28)} ${Y(-20)} ${X(25)} ${Y(2)}C${X(20)} ${Y(-12)} ${X(10)} ${Y(-18)} ${X(-4)} ${Y(-17)}C${X(-14)} ${Y(-14)} ${X(-21)} ${Y(-8)} ${X(-25)} ${Y(2)}Z" fill="${hair}"/>`);
  } else if (o.hair === 'wrap') {
    // a gele-style head wrap
    out.push(`<path d="M${X(-27)} ${Y(-4)}C${X(-34)} ${Y(-30)} ${X(-16)} ${Y(-48)} ${X(4)} ${Y(-46)}C${X(26)} ${Y(-46)} ${X(36)} ${Y(-28)} ${X(27)} ${Y(-4)}C${X(18)} ${Y(-16)} ${X(-18)} ${Y(-16)} ${X(-27)} ${Y(-4)}Z" fill="${o.wrap || GOLD}"/>`);
    out.push(`<path d="M${X(-18)} ${Y(-30)}C${X(-6)} ${Y(-38)} ${X(10)} ${Y(-38)} ${X(22)} ${Y(-26)}M${X(-22)} ${Y(-18)}C${X(-8)} ${Y(-26)} ${X(12)} ${Y(-26)} ${X(26)} ${Y(-14)}" stroke="rgba(0,0,0,.18)" stroke-width="${(2 * s).toFixed(1)}" fill="none" stroke-linecap="round"/>`);
    out.push(`<path d="M${X(18)} ${Y(-40)}C${X(34)} ${Y(-54)} ${X(44)} ${Y(-40)} ${X(32)} ${Y(-30)}Z" fill="${o.wrap || GOLD}"/>`);
  } else if (o.hair === 'puffs') {
    out.push(`<circle cx="${X(-22)}" cy="${Y(-24)}" r="${(11 * s).toFixed(1)}" fill="${hair}"/><circle cx="${X(22)}" cy="${Y(-24)}" r="${(11 * s).toFixed(1)}" fill="${hair}"/>`);
    out.push(`<path d="M${X(-24)} ${Y(-2)}C${X(-25)} ${Y(-28)} ${X(25)} ${Y(-28)} ${X(24)} ${Y(-2)}C${X(18)} ${Y(-16)} ${X(-18)} ${Y(-16)} ${X(-24)} ${Y(-2)}Z" fill="${hair}"/>`);
  }

  // beard and moustache
  if (o.beard) {
    out.push(`<path d="M${X(-24)} ${Y(2)}C${X(-24)} ${Y(24)} ${X(-12)} ${Y(32)} ${X(0)} ${Y(32)}C${X(12)} ${Y(32)} ${X(24)} ${Y(24)} ${X(24)} ${Y(2)}C${X(20)} ${Y(14)} ${X(12)} ${Y(18)} ${X(0)} ${Y(18)}C${X(-12)} ${Y(18)} ${X(-20)} ${Y(14)} ${X(-24)} ${Y(2)}Z" fill="${hair}"/>`);
    out.push(`<path d="M${X(-10)} ${Y(13)}C${X(-5)} ${Y(9)} ${X(5)} ${Y(9)} ${X(10)} ${Y(13)}C${X(5)} ${Y(12)} ${X(-5)} ${Y(12)} ${X(-10)} ${Y(13)}Z" fill="${hair}"/>`);
  }

  // face: eyebrows, closed happy eyes, nose, smile
  const w = (1 + 0.6 * s).toFixed(1);
  out.push(`<path d="M${X(-15)} ${Y(-9)}Q${X(-10)} ${Y(-13)} ${X(-4)} ${Y(-10)}M${X(4)} ${Y(-10)}Q${X(10)} ${Y(-13)} ${X(15)} ${Y(-9)}" stroke="${o.grey ? '#6e665d' : HAIR}" stroke-width="${(2.6 * s).toFixed(1)}" fill="none" stroke-linecap="round"/>`);
  out.push(`<path d="M${X(-14)} ${Y(-1)}Q${X(-9.5)} ${Y(-6)} ${X(-5)} ${Y(-1)}M${X(5)} ${Y(-1)}Q${X(9.5)} ${Y(-6)} ${X(14)} ${Y(-1)}" stroke="${INK}" stroke-width="${w}" fill="none" stroke-linecap="round"/>`);
  out.push(`<path d="M${X(0)} ${Y(1)}Q${X(3)} ${Y(7)} ${X(-1)} ${Y(9)}" stroke="${shade}" stroke-width="${w}" fill="none" stroke-linecap="round"/>`);
  out.push(`<path d="M${X(-8)} ${Y(14)}Q${X(0)} ${Y(23)} ${X(8)} ${Y(14)}Z" fill="#ffffff" stroke="${INK}" stroke-width="${(1.2 * s).toFixed(1)}" stroke-linejoin="round"/>`);
  if (!o.beard) out.push(`<ellipse cx="${X(-14)}" cy="${Y(8)}" rx="${(4 * s).toFixed(1)}" ry="${(2.5 * s).toFixed(1)}" fill="#e08a6d" opacity=".35"/><ellipse cx="${X(14)}" cy="${Y(8)}" rx="${(4 * s).toFixed(1)}" ry="${(2.5 * s).toFixed(1)}" fill="#e08a6d" opacity=".35"/>`);
  if (o.headphones) {
    out.push(`<path d="M${X(-26)} ${Y(2)}C${X(-28)} ${Y(-40)} ${X(28)} ${Y(-40)} ${X(26)} ${Y(2)}" stroke="${o.headphones}" stroke-width="${sw(4)}" fill="none" stroke-linecap="round"/>`);
    out.push(`<rect x="${X(-31)}" y="${Y(-6)}" width="${sw(9)}" height="${sw(16)}" rx="${sw(4)}" fill="${o.headphones}"/><rect x="${X(22)}" y="${Y(-6)}" width="${sw(9)}" height="${sw(16)}" rx="${sw(4)}" fill="${o.headphones}"/>`);
  }
  if (o.cap) {
    out.push(`<path d="M${X(-25)} ${Y(-8)}C${X(-26)} ${Y(-36)} ${X(26)} ${Y(-36)} ${X(25)} ${Y(-8)}Z" fill="${o.cap}"/><path d="M${X(10)} ${Y(-10)}C${X(26)} ${Y(-12)} ${X(38)} ${Y(-8)} ${X(40)} ${Y(-4)}H${X(10)}Z" fill="${o.cap}"/>`);
  }
  if (o.glasses) {
    out.push(`<g stroke="${INK}" stroke-width="${(1.6 * s).toFixed(1)}" fill="none"><circle cx="${X(-9.5)}" cy="${Y(-2)}" r="${(7 * s).toFixed(1)}"/><circle cx="${X(9.5)}" cy="${Y(-2)}" r="${(7 * s).toFixed(1)}"/><path d="M${X(-2.5)} ${Y(-3)}H${X(2.5)}"/></g>`);
  }
  if (o.earrings) {
    out.push(`<circle cx="${X(-25)}" cy="${Y(12)}" r="${(2.6 * s).toFixed(1)}" fill="${GOLD}"/><circle cx="${X(25)}" cy="${Y(12)}" r="${(2.6 * s).toFixed(1)}" fill="${GOLD}"/>`);
  }
  return out.join('');
}

// ---------- props and scenery (absolute coordinates in the 160 x 150 picture) ----------

const PAPER = '#ffffff';
const LINE = '#c9d6cf';
const SKYC = 'var(--vp-sky,#a9d3e6)';
const SAND = '#ecd29a';
const LEAF = '#e0893f';
const hands = (x1, x2, y, skin) => `<circle cx="${x1}" cy="${y}" r="5.5" fill="${skin}"/><circle cx="${x2}" cy="${y}" r="5.5" fill="${skin}"/>`;
const book = (cx, cy, c) => `<g transform="rotate(-6 ${cx} ${cy})"><rect x="${cx - 15}" y="${cy - 11}" width="30" height="22" rx="2" fill="${c}"/><rect x="${cx - 13}" y="${cy - 9}" width="26" height="18" rx="1" fill="${PAPER}"/><path d="M${cx} ${cy - 9}V${cy + 9}" stroke="${LINE}" stroke-width="1.2"/><path d="M${cx - 10} ${cy - 4}h7M${cx - 10} ${cy}h7M${cx + 3} ${cy - 4}h7M${cx + 3} ${cy}h7" stroke="${LINE}" stroke-width="1.4" stroke-linecap="round"/></g>`;
const laptop = (cx, cy) => `<rect x="${cx - 18}" y="${cy - 14}" width="36" height="22" rx="2.5" fill="#2b3a44"/><rect x="${cx - 15}" y="${cy - 11}" width="30" height="16" rx="1" fill="${SKYC}"/><path d="M${cx - 23} ${cy + 8}h46l-4 5h-38z" fill="#93a3ad"/>`;
const passport = (cx, cy) => `<g transform="rotate(8 ${cx} ${cy})"><rect x="${cx - 12}" y="${cy - 16}" width="24" height="32" rx="3" fill="${GREEN}"/><circle cx="${cx}" cy="${cy - 3}" r="6" fill="none" stroke="${GOLD}" stroke-width="1.8"/><path d="M${cx - 6} ${cy + 9}h12" stroke="${GOLD}" stroke-width="1.8" stroke-linecap="round"/></g>`;
const keys = (cx, cy) => `<circle cx="${cx}" cy="${cy}" r="6" fill="none" stroke="${GOLD}" stroke-width="2.4"/><path d="M${cx + 4} ${cy + 4}l9 9m-3 -3l3 -3m0 6l3 -3" stroke="${GOLD}" stroke-width="2.4" stroke-linecap="round"/><path d="M${cx - 5} ${cy + 4}l-5 11" stroke="#9aa6ad" stroke-width="2.4" stroke-linecap="round"/>`;
const suitcase = (x, base, c) => `<path d="M${x + 9} ${base - 46}v-7a3 3 0 0 1 3 -3h10a3 3 0 0 1 3 3v7" stroke="#55453a" stroke-width="3" fill="none"/>` + `<rect x="${x}" y="${base - 47}" width="34" height="44" rx="5" fill="${c}"/><path d="M${x + 11} ${base - 47}v44M${x + 23} ${base - 47}v44" stroke="rgba(0,0,0,.18)" stroke-width="3"/>` + `<circle cx="${x + 7}" cy="${base - 1}" r="3" fill="#55453a"/><circle cx="${x + 27}" cy="${base - 1}" r="3" fill="#55453a"/>`;
const calendarBoard = (x, y, w, h) => `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="6" fill="${PAPER}" stroke="${LINE}" stroke-width="1.5"/>` + `<path d="M${x} ${y + 6}a6 6 0 0 1 6 -6h${w - 12}a6 6 0 0 1 6 6v${h * 0.16}h${-w}z" fill="${GREEN}"/>` + `<rect x="${x + w * 0.25}" y="${y - 5}" width="5" height="11" rx="2.5" fill="${GOLD}"/><rect x="${x + w * 0.7}" y="${y - 5}" width="5" height="11" rx="2.5" fill="${GOLD}"/>` + [0, 1, 2, 3].map(r => [0, 1, 2, 3, 4].map(c => {
  const on = r === 1 && c === 3;
  const cx = x + 8 + c * (w - 16) / 5;
  const cy = y + h * 0.3 + r * h * 0.17;
  return `<rect x="${cx.toFixed(1)}" y="${cy.toFixed(1)}" width="${((w - 16) / 5 - 3).toFixed(1)}" height="${(h * 0.11).toFixed(1)}" rx="1.5" fill="${on ? GOLD : '#e3ebe6'}"/>` + (on ? `<circle cx="${(cx + (w - 16) / 10 - 1.5).toFixed(1)}" cy="${(cy + h * 0.055).toFixed(1)}" r="${(h * 0.1).toFixed(1)}" fill="none" stroke="${CORAL}" stroke-width="2"/>` : '');
}).join('')).join('');
const house = function (cx, base, s, wall, roof) {
  if (wall === void 0) {
    wall = '#f3ead8';
  }
  if (roof === void 0) {
    roof = CORAL;
  }
  return `<path d="M${cx - 30 * s} ${base}V${base - 36 * s}H${cx + 30 * s}V${base}Z" fill="${wall}"/><path d="M${cx - 38 * s} ${base - 34 * s}L${cx} ${base - 62 * s}L${cx + 38 * s} ${base - 34 * s}Z" fill="${roof}"/>` + `<rect x="${cx - 7 * s}" y="${base - 22 * s}" width="${14 * s}" height="${22 * s}" rx="${2 * s}" fill="${GREEN}"/><rect x="${cx + 13 * s}" y="${base - 28 * s}" width="${11 * s}" height="${10 * s}" fill="${SKYC}"/><rect x="${cx - 24 * s}" y="${base - 28 * s}" width="${11 * s}" height="${10 * s}" fill="${SKYC}"/>`;
};
const plane = function (cx, cy, s, rot) {
  if (rot === void 0) {
    rot = -14;
  }
  return `<g transform="translate(${cx} ${cy}) rotate(${rot}) scale(${s})"><path d="M-24 0c0 -4 6 -6 14 -6h22c6 0 12 3 12 6s-6 6 -12 6h-22c-8 0 -14 -2 -14 -6z" fill="${PAPER}" stroke="${LINE}" stroke-width="1.2"/><path d="M-2 -2l-10 -16h7l14 16z M-2 4l-8 13h7l11 -13z M-20 -2l-5 -9h5l5 9z" fill="${GREEN}"/><circle cx="16" cy="-1" r="2" fill="${SKYC}"/><circle cx="9" cy="-1" r="2" fill="${SKYC}"/><circle cx="2" cy="-1" r="2" fill="${SKYC}"/></g>`;
};
const ngFlag = (x, y) => `<path d="M${x} ${y}v40" stroke="#6d6158" stroke-width="2"/><rect x="${x}" y="${y}" width="24" height="15" fill="#fff"/><rect x="${x}" y="${y}" width="8" height="15" fill="#1d8348"/><rect x="${x + 16}" y="${y}" width="8" height="15" fill="#1d8348"/>`;
const map = (cx, cy) => `<g transform="rotate(-5 ${cx} ${cy})"><path d="M${cx - 26} ${cy - 15}l17 -4l17 4l18 -4v32l-18 4l-17 -4l-17 4z" fill="#f6efdc" stroke="${LINE}" stroke-width="1.2"/><path d="M${cx - 9} ${cy - 19}v32M${cx + 8} ${cy - 15}v32" stroke="${LINE}" stroke-width="1.2"/><path d="M${cx - 20} ${cy + 8}c8 -2 6 -14 16 -12s6 10 18 4" stroke="${CORAL}" stroke-width="2" stroke-dasharray="3 3" fill="none"/><circle cx="${cx + 15}" cy="${cy - 1}" r="3.5" fill="${CORAL}"/></g>`;
const sun = (cx, cy, r) => `<g stroke="${GOLD}" stroke-width="3" stroke-linecap="round">` + [0, 45, 90, 135, 180, 225, 270, 315].map(a => {
  const t = a * Math.PI / 180;
  return `<path d="M${(cx + Math.cos(t) * (r + 5)).toFixed(1)} ${(cy + Math.sin(t) * (r + 5)).toFixed(1)}L${(cx + Math.cos(t) * (r + 11)).toFixed(1)} ${(cy + Math.sin(t) * (r + 11)).toFixed(1)}"/>`;
}).join('') + `</g><circle cx="${cx}" cy="${cy}" r="${r}" fill="${GOLD}"/>`;
const cloud = (cx, cy, s) => `<path d="M${cx - 26 * s} ${cy}a10 10 0 0 1 8 -16a14 14 0 0 1 26 -4a11 11 0 0 1 18 10a8 8 0 0 1 0 10z" fill="#ffffff" stroke="${LINE}" stroke-width="1.2" transform="translate(0 0)"/>`;
const snow = (cx, cy, r) => `<g stroke="${SKYC}" stroke-width="2.4" stroke-linecap="round"><path d="M${cx} ${cy - r}v${2 * r}M${cx - r * 0.87} ${cy - r / 2}l${r * 1.74} ${r}M${cx - r * 0.87} ${cy + r / 2}l${r * 1.74} ${-r}"/></g>`;
const notes = (x, base, n) => Array.from({
  length: n
}, (_, i) => `<rect x="${x}" y="${base - 7 - i * 6}" width="34" height="7" rx="1.5" fill="${i % 2 ? '#3f8f5a' : '#2e7a4c'}"/><circle cx="${x + 17}" cy="${base - 3.5 - i * 6}" r="2" fill="#c9e6cf"/>`).join('');
const coins = (x, base, n) => Array.from({
  length: n
}, (_, i) => `<ellipse cx="${x}" cy="${base - 3 - i * 4}" rx="9" ry="3.5" fill="${GOLD}" stroke="#b8862f" stroke-width="1"/>`).join('');

// the person taking the quiz, and the people around them
const YOU = {
  male: {
    sex: 'male',
    skin: 'brown',
    shirt: GREEN,
    hair: 'short',
    beard: true
  },
  female: {
    sex: 'female',
    skin: 'warm',
    shirt: CORAL,
    hair: 'braids',
    earrings: true
  }
};
const PARTNER = {
  male: {
    sex: 'female',
    skin: 'deep',
    shirt: TEAL,
    hair: 'wrap',
    wrap: GOLD,
    earrings: true
  },
  female: {
    sex: 'male',
    skin: 'deep',
    shirt: NAVY,
    hair: 'fade',
    beard: true
  }
};
const GIRL = {
  skin: 'warm',
  shirt: PLUM,
  hair: 'puffs',
  scale: 0.62
};
const BOY = {
  skin: 'brown',
  shirt: GOLD,
  hair: 'fade',
  scale: 0.6
};
const handOf = o => SKIN[o.skin || 'brown'][0];

// the same person at each age band
const AGES = {
  male: {
    '18-24': {
      ...YOU.male,
      beard: false,
      hair: 'fade',
      outfit: 'hoodie',
      shirt: TEAL,
      headphones: NAVY
    },
    '25-29': {
      ...YOU.male,
      shirt: CORAL,
      straps: GOLD
    },
    '30-34': {
      ...YOU.male,
      outfit: 'collar',
      shirt: NAVY
    },
    '35-39': {
      ...YOU.male,
      outfit: 'blazer',
      shirt: GREEN
    },
    '40-44': {
      ...YOU.male,
      outfit: 'blazer',
      shirt: NAVY,
      glasses: true
    },
    '45-plus': {
      ...YOU.male,
      outfit: 'cardigan',
      shirt: PLUM,
      glasses: true,
      grey: true
    }
  },
  female: {
    '18-24': {
      ...YOU.female,
      hair: 'puffs',
      outfit: 'hoodie',
      shirt: TEAL,
      headphones: NAVY
    },
    '25-29': {
      ...YOU.female,
      shirt: CORAL,
      straps: GOLD
    },
    '30-34': {
      ...YOU.female,
      outfit: 'collar',
      shirt: GREEN
    },
    '35-39': {
      ...YOU.female,
      outfit: 'blazer',
      shirt: NAVY
    },
    '40-44': {
      ...YOU.female,
      hair: 'wrap',
      wrap: GOLD,
      outfit: 'blazer',
      shirt: PLUM,
      glasses: true
    },
    '45-plus': {
      ...YOU.female,
      outfit: 'cardigan',
      shirt: TEAL,
      glasses: true,
      grey: true
    }
  }
};
const D = {};
/** A male and a female version of a card ("…-{sex}" in the quiz) */
const both = (name, label, alt, draw, tint) => {
  D[name + '-male'] = [label, alt, () => draw(YOU.male, 'male'), tint];
  D[name + '-female'] = [label, alt, () => draw(YOU.female, 'female'), tint];
};
const one = (name, label, alt, draw, tint) => {
  D[name] = [label, alt, draw, tint];
};

// the gender question
one('you-male', 'Man', 'A smiling man with a beard', () => character(100, {
  ...YOU.male,
  scale: 1.2
}), '#e3edf6');
one('you-female', 'Woman', 'A smiling woman with braids', () => character(100, {
  ...YOU.female,
  scale: 1.2
}), '#f8e6ee');

// who is moving with you
both('hh-solo', 'Just me', 'You on your own', y => character(100, {
  ...y,
  scale: 1.16
}));
both('hh-partner', 'You and your partner', 'A couple', (y, sex) => sex === 'male' ? character(58, {
  ...PARTNER.male,
  scale: 1.0
}) + character(116, {
  ...y,
  scale: 1.06
}) : character(116, {
  ...PARTNER.female,
  scale: 1.06
}) + character(58, {
  ...y,
  scale: 1.0
}));
both('hh-family', 'Family', 'A couple and two children', (y, sex) => (sex === 'male' ? character(46, {
  ...PARTNER.male,
  scale: 0.92
}) + character(122, {
  ...y,
  scale: 0.96
}) : character(122, {
  ...PARTNER.female,
  scale: 0.96
}) + character(46, {
  ...y,
  scale: 0.92
})) + character(68, {
  ...GIRL,
  base: 166
}) + character(102, {
  ...BOY,
  base: 168
}));
both('hh-parent', 'You and your children', 'A parent and two children', y => character(96, {
  ...y,
  scale: 1.02
}) + character(46, {
  ...GIRL,
  base: 160,
  scale: 0.68
}) + character(140, {
  ...BOY,
  base: 164,
  scale: 0.64
}));

// how old are you: the same person at each age
for (const band of Object.keys(AGES.male)) {
  D['age-' + band + '-male'] = [band, 'You at this age', () => character(100, {
    ...AGES.male[band],
    scale: 1.12
  })];
  D['age-' + band + '-female'] = [band, 'You at this age', () => character(100, {
    ...AGES.female[band],
    scale: 1.12
  })];
}

// main reason for moving
both('why-work', 'Work', 'You with a laptop', y => character(96, {
  ...y,
  outfit: 'blazer',
  scale: 1.04
}) + laptop(96, 128) + hands(74, 118, 136, handOf(y)));
both('why-study', 'Study', 'You with a book and a backpack', y => character(96, {
  ...y,
  straps: GOLD,
  scale: 1.04
}) + book(96, 130, NAVY) + hands(80, 112, 134, handOf(y)));
both('why-business', 'Business', 'You at your shop counter', y => `<path d="M22 44v14M40 44v14" stroke="#6d6158" stroke-width="1.6"/><rect x="12" y="56" width="38" height="18" rx="3" fill="${GOLD}"/><text x="31" y="69" text-anchor="middle" font-size="10" font-weight="800" fill="#fff" font-family="inherit">OPEN</text>` + character(100, {
  ...y,
  scale: 1.0
}) + `<rect x="0" y="128" width="160" height="22" fill="#b98a5a"/><rect x="0" y="126" width="160" height="5" fill="#9c7247"/>` + coins(132, 126, 3));
both('why-family', 'Joining family', 'You with an older relative', y => character(52, {
  skin: 'deep',
  shirt: PLUM,
  hair: 'wrap',
  wrap: TEAL,
  grey: true,
  outfit: 'cardigan',
  glasses: true,
  earrings: true,
  scale: 0.92
}) + character(112, {
  ...y,
  scale: 1.0
}) + `<path d="M80 36c-4 -6 -14 -3 -12 4c1 5 12 11 12 11s11 -6 12 -11c2 -7 -8 -10 -12 -4z" fill="${CORAL}"/>`);
both('why-children', 'My children', 'You with your child in school uniform', y => character(108, {
  ...y,
  scale: 1.0
}) + character(48, {
  skin: 'warm',
  shirt: NAVY,
  outfit: 'uniform',
  tie: CORAL,
  hair: 'puffs',
  straps: GOLD,
  scale: 0.7,
  base: 154
}));

// long-term plan
both('plan-settle', 'Settle there', 'You with the keys to a house', y => house(42, 112, 1.0) + character(112, {
  ...y,
  scale: 1.0
}) + keys(140, 118) + `<circle cx="138" cy="126" r="5.5" fill="${handOf(y)}"/>`);
both('plan-passport', 'Second passport', 'You holding up a passport', y => character(92, {
  ...y,
  scale: 1.04
}) + passport(136, 106) + `<circle cx="132" cy="122" r="5.5" fill="${handOf(y)}"/>`);
both('plan-return', 'Come back home', 'You, a plane and the Nigerian flag', y => plane(36, 92, 0.62, 12) + ngFlag(14, 104) + character(104, {
  ...y,
  scale: 1.04
}));

// when do you hope to move
both('when-soon', 'Within a year', 'You with a packed suitcase', y => character(84, {
  ...y,
  scale: 1.04
}) + suitcase(118, 150, CORAL) + plane(134, 50, 0.6, -16));
both('when-later', 'In 1–3 years', 'You and a calendar', y => calendarBoard(10, 36, 58, 50) + character(110, {
  ...y,
  scale: 1.02
}));
both('when-exploring', 'Just exploring', 'You looking at a map', y => character(96, {
  ...y,
  scale: 1.04
}) + map(96, 128) + hands(72, 122, 132, handOf(y)));

// how old are the children
one('kid-under-5', 'Under 5', 'A toddler', () => character(84, {
  skin: 'warm',
  shirt: CORAL,
  hair: 'puffs',
  scale: 0.86
}));
one('kid-5-11', '5–11', 'A child in school uniform', () => character(84, {
  skin: 'brown',
  shirt: NAVY,
  outfit: 'uniform',
  tie: CORAL,
  hair: 'fade',
  straps: GOLD,
  scale: 0.98
}));
one('kid-12-17', '12–17', 'A teenager with headphones', () => character(88, {
  skin: 'deep',
  shirt: TEAL,
  outfit: 'hoodie',
  hair: 'braids',
  headphones: PLUM,
  earrings: true,
  scale: 1.06
}));
one('kid-18-plus', '18 or older', 'A young adult in a cap', () => character(92, {
  skin: 'brown',
  shirt: GOLD,
  hair: 'fade',
  cap: GREEN,
  scale: 1.14
}));

// how many children
const KIDS = [{
  skin: 'warm',
  shirt: PLUM,
  hair: 'puffs'
}, {
  skin: 'brown',
  shirt: GOLD,
  hair: 'fade'
}, {
  skin: 'deep',
  shirt: TEAL,
  hair: 'braids'
}, {
  skin: 'light',
  shirt: CORAL,
  hair: 'fade'
}];
one('kids-1', '1', 'One child', () => character(84, {
  ...KIDS[0],
  scale: 0.86
}));
one('kids-2', '2', 'Two children', () => character(56, {
  ...KIDS[0],
  scale: 0.74
}) + character(112, {
  ...KIDS[1],
  scale: 0.78
}));
one('kids-3', '3', 'Three children', () => character(36, {
  ...KIDS[0],
  scale: 0.62
}) + character(124, {
  ...KIDS[2],
  scale: 0.64
}) + character(80, {
  ...KIDS[1],
  scale: 0.68,
  base: 156
}));
one('kids-4-plus', '4 or more', 'Four children', () => character(28, {
  ...KIDS[0],
  scale: 0.54
}) + character(70, {
  ...KIDS[2],
  scale: 0.56
}) + character(112, {
  ...KIDS[3],
  scale: 0.56
}) + character(150, {
  ...KIDS[1],
  scale: 0.54
}) + `<circle cx="140" cy="40" r="13" fill="${GREEN}"/><path d="M134 40h12M140 34v12" stroke="#fff" stroke-width="3" stroke-linecap="round"/>`);

// weather
one('weather-warm', 'Warm all year', 'A sunny beach with a palm tree', () => sun(118, 54, 14) + `<rect x="0" y="96" width="160" height="22" fill="${SKYC}"/><path d="M0 118c30 -6 60 -4 90 2s50 4 70 0V150H0z" fill="${SAND}"/>` + `<path d="M40 150c2 -26 4 -48 12 -70" stroke="#8a6141" stroke-width="5" fill="none" stroke-linecap="round"/>` + `<path d="M52 80c-14 -10 -30 -6 -36 4c12 -4 24 -2 36 -4zM52 80c-2 -16 -16 -24 -28 -22c10 4 20 12 28 22zM52 80c10 -14 26 -14 34 -6c-12 0 -24 2 -34 6zM52 80c14 -2 26 6 28 16c-8 -8 -18 -12 -28 -16z" fill="${GREEN}"/>`);
one('weather-mild', 'Mild, with seasons', 'An autumn tree, sun and cloud', () => sun(104, 52, 12) + cloud(114, 70, 1) + `<path d="M48 150v-40" stroke="#8a6141" stroke-width="6" stroke-linecap="round"/>` + `<circle cx="48" cy="86" r="28" fill="${LEAF}"/><circle cx="30" cy="96" r="16" fill="#c96a2e"/><circle cx="66" cy="98" r="15" fill="#d9a441"/>` + `<path d="M100 120q4 -4 8 0q-4 4 -8 0zM122 134q4 -4 8 0q-4 4 -8 0zM86 138q4 -4 8 0q-4 4 -8 0z" fill="${LEAF}"/><rect x="0" y="146" width="160" height="4" fill="#cfe0c9"/>`);
one('weather-any', "I don't mind", 'Sun, cloud, rain and snow', () => sun(46, 54, 13) + cloud(110, 64, 1) + `<g stroke="${SKYC}" stroke-width="3" stroke-linecap="round"><path d="M94 80l-4 10M106 80l-4 10M118 80l-4 10"/></g>` + snow(40, 116, 9) + snow(78, 128, 7) + snow(128, 118, 9));

// savings, in US dollars
const SAVE = [['under-5k', 'Under $5,000', 1, 0], ['5k-15k', '$5,000–15,000', 2, 1], ['15k-30k', '$15,000–30,000', 3, 2], ['30k-60k', '$30,000–60,000', 4, 3], ['60k-plus', 'More than $60,000', 5, 4]];
for (const [v, label, n] of SAVE) {
  D['save-' + v] = [label, label + ' saved', () => {
    let svg = coins(36, 146, 2 + n);
    for (let i = 0; i < n; i++) svg += notes(52 + i * 20 - (n - 1) * 4, 146, 3 + i * 3);
    return svg;
  }];
}

// years of experience: the same steps, standing higher up
const EXP = [['0-2', '0–2 years', 0], ['3-5', '3–5 years', 1], ['6-10', '6–10 years', 2], ['10-plus', 'More than 10 years', 3]];
for (const [v, label, step] of EXP) {
  const draw = y => {
    let svg = '';
    for (let i = 0; i < 4; i++) svg += `<rect x="${12 + i * 34}" y="${134 - i * 19}" width="34" height="${16 + i * 19}" fill="${i <= step ? 'rgba(46,122,76,.22)' : 'rgba(120,140,130,.14)'}"/><rect x="${12 + i * 34}" y="${134 - i * 19}" width="34" height="5" fill="${i <= step ? GREEN : LINE}"/>`;
    svg += `<path d="M146 56v24" stroke="#6d6158" stroke-width="2"/><path d="M146 56h12l-4 6l4 6h-12z" fill="${GOLD}"/>`;
    return svg + character(29 + step * 34, {
      ...y,
      scale: 0.52,
      base: 134 - step * 19
    });
  };
  D['exp-' + v + '-male'] = [label, 'You on the career steps', () => draw(YOU.male)];
  D['exp-' + v + '-female'] = [label, 'You on the career steps', () => draw(YOU.female)];
}

// languages
const bubble = (x, y, w, text, c) => `<path d="M${x} ${y}h${w}a10 10 0 0 1 10 10v16a10 10 0 0 1 -10 10h${-w + 16}l-10 9v-9h-6a10 10 0 0 1 -10 -10v-16a10 10 0 0 1 10 -10z" fill="${c}"/>` + `<text x="${x + w / 2}" y="${y + 23}" text-anchor="middle" font-size="15" font-weight="700" fill="#ffffff" font-family="inherit">${text}</text>`;
one('lang-english', 'English', 'A speech bubble saying Hello', () => bubble(34, 60, 92, 'Hello!', GREEN));
one('lang-french', 'French', 'A speech bubble saying Bonjour', () => bubble(30, 60, 100, 'Bonjour !', NAVY));
one('lang-german', 'German', 'A speech bubble saying Hallo', () => bubble(34, 60, 92, 'Hallo!', CORAL));
one('lang-other', 'Another language', 'Speech bubbles in other languages', () => bubble(14, 40, 64, 'Hola', PLUM) + bubble(80, 74, 64, 'Ciao', TEAL) + bubble(30, 104, 50, '…', GOLD));
const resolve = (name, sex) => String(name || '').replace('{sex}', sex === 'female' ? 'female' : 'male');

/** [name, label] for pickers: "…-{sex}" for the pairs, so one choice follows the person's gender */
export const PORTRAIT_NAMES = (() => {
  const seen = new Set();
  const out = [];
  for (const name of Object.keys(D)) {
    const m = name.match(/^(.*)-(male|female)$/);
    const pick = m && D[m[1] + '-male'] && D[m[1] + '-female'] && !name.startsWith('you-') ? m[1] + '-{sex}' : name;
    if (seen.has(pick)) continue;
    seen.add(pick);
    out.push([pick, D[resolve(pick, 'male')][0] + (pick.includes('{sex}') ? ' (follows gender)' : '')]);
  }
  return out;
})();

/** Is this a portrait name ("{sex}" names included)? */
export const isPortrait = name => !!name && !!D[resolve(name, 'male')];
export const portraitAlt = (name, sex) => {
  const d = D[resolve(name, sex)];
  return d ? d[1] : '';
};

/** Deeper versions of the card tints, for dark mode */
const DARK_TINTS = {
  '#e3edf6': '#1f3a56',
  '#f8e6ee': '#4d2438'
};

/** The card's background tint for this portrait, if it has its own (dark: the deeper version for dark mode) */
export const portraitTint = function (name, dark) {
  if (dark === void 0) {
    dark = false;
  }
  const d = D[resolve(name, 'male')];
  if (!d || !d[3]) return null;
  return dark ? DARK_TINTS[d[3]] || null : d[3];
};

/** SVG markup for a portrait; "{sex}" in the name becomes the given sex (male if not known) */
export function portrait(name, sex) {
  const d = D[resolve(name, sex)];
  if (!d) return '';
  return `<svg class="VPortrait" viewBox="0 0 160 150" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" preserveAspectRatio="xMidYMax meet">${d[2]()}</svg>`;
}
flarum.reg.add('voucha-assessments', 'common/portraits', { PORTRAIT_NAMES: PORTRAIT_NAMES,isPortrait: isPortrait,portraitAlt: portraitAlt,portraitTint: portraitTint,portrait: portrait, });